"use client";
import { mergeClasses } from "@fluentui/react-components";
import {
  Button,
  Label,
  Tag,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ActivityContent } from "@skanska/vsa-activity-content";
import { UserInformation } from "@skanska/vsa-people-picker";
import { useMsal } from "@skanska/vsa-client-auth";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import { ActivityHistoryDialog } from "../components/activity/ActivityHistoryDialog";
import ActivityToolsTable from "../components/activity/ActivityToolsTable";
import { ActivityWithMirroredChildrenMessageBar } from "../components/activity/ActivityWithMirroredChildrenMessageBar";
import { ComplianceWarningMessageBar } from "../components/activity/ComplianceWarningMessageBar";
import { CreateDraftButton } from "../components/activity/CreateDraftButton";
import { CreateMirroredActivityButton } from "../components/activity/CreateMirroredActivityButton";
import { MirroredActivityMessageBar } from "../components/activity/MirroredActivityMessageBar";
import { MirroredAndSharedInfoCard } from "../components/activity/MirroredAndSharedInfoCard";
import { OldActivityVersionMessageBar } from "../components/activity/OldActivityVersionMessageBar";
import { useActivtyContentClasses } from "../components/activity/useActivtyContentClasses";
import { CheckoutMessageBar } from "../components/CheckoutMessageBar";
import { DisplayField } from "../components/DisplayField";
import { ActivityOverviewSkeleton } from "../components/skeletons/ActivityOverviewSkeleton";
import { StickyTopBannerStack } from "../components/StickyTopBannerStack";
import { ProcessFieldUniqueName } from "../constants/Fields";
import {
  useGetCurrentActivityDraft,
  useGetActivityFields,
  useGetPublishedActivity,
} from "../hooks/useActivities";
import { useActivityImageData } from "../hooks/useActivityImageData";
import { useApplyTableWidths } from "../hooks/useApplyTableWidths";
import { useGetUserAccessLevel } from "../hooks/useGetUserAccessLevel";
import { ClientError } from "../models/ClientError";
import { FieldTypes } from "../models/FieldTypes";
import { UrlFieldValue } from "../models/FieldValues";
import { FlatVsaaTag, VsaaTag } from "../models/VsaaTag";
import {
  getBooleanFieldValue,
  getTagFieldValue,
  getTypedFieldType,
  getUserFieldValue,
} from "../services/FieldService";
import {
  getPublishedActivityFieldNames,
  groupComplianceTagsByDocumentType,
} from "../services/ActivityOverviewService";
import { getClientAppComplianceMatrixUrl } from "../services/EndUserClientService";

const useClasses = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  activityWrapper: {
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "fit-content(700px) minmax(0, 1fr)",
    columnGap: tokens.spacingHorizontalXL,
    rowGap: tokens.spacingVerticalL,
    alignItems: "start",
    padding: tokens.spacingVerticalM,
    "@media (max-width: 900px)": {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    width: "fit-content",
    maxWidth: "700px",
    minWidth: 0,
    gap: tokens.spacingVerticalM,
    minHeight: "100%",
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    justifySelf: "stretch",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: "auto",
    gap: "10px",
    paddingTop: tokens.spacingVerticalM,
    flexWrap: "wrap",
  },
  labels: {
    fontWeight: "bold",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    maxWidth: "700px",
    minWidth: 0,
  },
  requirementsSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  complianceTagLinks: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
  },
  sectionLink: {
    color: "inherit",
    textDecoration: "none",
  },
  sectionLabelLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: "underline",
    width: "fit-content",
    cursor: "pointer",
    ":hover": {
      color: tokens.colorBrandForegroundLinkHover,
    },
  },
  clickableSectionLabel: {
    cursor: "pointer",
    color: "inherit",
  },
  complianceTag: {
    cursor: "pointer",
  },
});

function ActivityOverviewPage() {
  const classes = useClasses();
  const contentClasses = useActivtyContentClasses();
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const { instance } = useMsal();
  const currentUser = instance.getActiveAccount();

  const accessLevel = useGetUserAccessLevel();

  // Apply table widths from data-table-width attributes
  if (!id) {
    const missingIdError: ClientError = {
      message: "Activity id is missing",
      name: "Activity id is missing",
    };

    throw missingIdError;
  }

  const {
    data: activity,
    isLoading,
    isRefetching,
  } = useGetPublishedActivity(id);
  const { data: currentDraft } = useGetCurrentActivityDraft(
    activity?.activityId
  );

  const isCurrentUsersDraft = useMemo(() => {
    if (!currentDraft) {
      return true;
    }

    const userEntraId = currentUser?.idTokenClaims?.oid;

    if (!userEntraId) {
      return false;
    }

    return userEntraId === currentDraft.createdBy?.id;
  }, [currentDraft, currentUser]);

  const isMirrored = !!activity?.mirroredActivityId;
  useApplyTableWidths(contentRef, activity);
  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetActivityFields();

  const htmlContent = activity?.description?.serializedHtml;
  const activityTools = activity?.tools ?? [];
  const complianceTags =
    (getTagFieldValue("compliance", activity?.fieldValues) as FlatVsaaTag[]) ??
    [];
  const hasStickyTopBanners =
    (activity && !activity.isCurrentVersion && activity.activityId) ||
    (!isCurrentUsersDraft && currentDraft?.createdBy) ||
    complianceTags.length > 0 ||
    isMirrored ||
    Boolean(activity?.mirroredChildren?.length);
  const groupedComplianceTags =
    groupComplianceTagsByDocumentType(complianceTags);
  const publishedFieldNames = getPublishedActivityFieldNames(
    activity?.fieldValues,
    viewDefinition,
    ["compliance"]
  );
  const tagFieldNames = publishedFieldNames.filter((fieldName) => {
    const field = viewDefinition?.fields.find(
      (item) => item.uniqueName === fieldName
    );

    return field ? getTypedFieldType(field.type) === FieldTypes.Tag : false;
  });
  const nonTagFieldNames = publishedFieldNames.filter(
    (fieldName) => !tagFieldNames.includes(fieldName)
  );

  const getFieldDisplayField = (fieldName: string) => {
    if (!viewDefinition) {
      return null;
    }

    const field = viewDefinition.fields.find((f) => f.uniqueName === fieldName);

    if (!field) {
      return null;
    }

    const fieldType = getTypedFieldType(field.type);

    const fieldValue:
      | boolean
      | VsaaTag[]
      | UserInformation
      | UserInformation[]
      | UrlFieldValue
      | undefined =
      fieldType === FieldTypes.BooleanCheckBox ||
      fieldType === FieldTypes.BooleanSwitch
        ? getBooleanFieldValue(fieldName, activity?.fieldValues)
        : fieldType === FieldTypes.Tag
          ? getTagFieldValue(fieldName, activity?.fieldValues)
          : fieldType === FieldTypes.User
            ? getUserFieldValue(fieldName, activity?.fieldValues)
            : fieldType === FieldTypes.Url
              ? (activity?.fieldValues?.urls[fieldName] ?? undefined)
              : undefined;

    if (fieldValue === undefined) {
      return null;
    }

    return (
      <>
        <Label className={classes.labels}>{field.displayName}</Label>
        <DisplayField
          label={field.displayName}
          value={fieldValue}
          type={fieldType}
          showPath={field.uniqueName === ProcessFieldUniqueName}
        />
      </>
    );
  };

  return (
    <div className={classes.root}>
      {hasStickyTopBanners && (
        <StickyTopBannerStack>
          {activity && !activity.isCurrentVersion && activity.activityId && (
            <OldActivityVersionMessageBar activityId={activity.activityId} />
          )}

          {!isCurrentUsersDraft && currentDraft?.createdBy && (
            <CheckoutMessageBar checkedOutTo={currentDraft.createdBy} />
          )}

          {activity && <ComplianceWarningMessageBar activity={activity} />}

          {isMirrored && (
            <MirroredActivityMessageBar
              activityId={activity.mirroredActivityId!}
            />
          )}
          <ActivityWithMirroredChildrenMessageBar
            childActivityCount={activity?.mirroredChildren?.length || 0}
          />
        </StickyTopBannerStack>
      )}
      {!isLoading && !isRefetching && !viewDefinitionLoading ? (
        <div className={classes.activityWrapper}>
          <div className={classes.leftColumn}>
            <div className={classes.contentWrapper}>
              <h1>{activity?.title}</h1>
              <div ref={contentRef} className={contentClasses.activityContent}>
                {htmlContent && (
                  <ActivityContent
                    htmlContent={htmlContent}
                    useImageData={useActivityImageData}
                  />
                )}
              </div>
            </div>

            {accessLevel > 1 && (
              <div className={classes.buttons}>
                <Button onClick={() => navigate(-1)}>{t("back")}</Button>

                {activity &&
                  activity.activityId &&
                  viewDefinition &&
                  (accessLevel >= 3 || activity.isCurrentVersion) && (
                    <CreateDraftButton
                      activity={activity}
                      activityFields={viewDefinition.fields}
                    />
                  )}
                {activity &&
                  activity.activityId &&
                  activity.isCurrentVersion &&
                  viewDefinition &&
                  !isMirrored &&
                  accessLevel >= 3 && (
                    <CreateMirroredActivityButton
                      activity={activity}
                      activityFields={viewDefinition.fields}
                    />
                  )}
                {activity &&
                  activity.activityId &&
                  activity.isCurrentVersion && (
                    <ActivityHistoryDialog activityId={activity.activityId} />
                  )}
              </div>
            )}
          </div>

          <div className={classes.rightColumn}>
            {groupedComplianceTags.length > 0 && (
              <div className={classes.requirementsSection}>
                <h3>{t("activityOverviewRequirementsHeading")}</h3>
                {groupedComplianceTags.map((group) => (
                  <Fragment key={group.documentType}>
                    <a
                      className={classes.sectionLabelLink}
                      href={getClientAppComplianceMatrixUrl(
                        group.documentTypeNodeId
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Label
                        className={mergeClasses(
                          classes.labels,
                          classes.clickableSectionLabel
                        )}
                      >
                        {group.documentType}
                      </Label>
                    </a>
                    <div className={classes.complianceTagLinks}>
                      {group.topNodes.map((topNode) => (
                        <Fragment key={topNode.id}>
                          <Tag
                            dismissible={false}
                            shape="rounded"
                            size="extra-small"
                          >
                            {topNode.title}
                          </Tag>
                        </Fragment>
                      ))}
                    </div>
                  </Fragment>
                ))}
              </div>
            )}

            {tagFieldNames.length > 0 && (
              <div className={classes.requirementsSection}>
                <h3>{t("activitySectionTags")}</h3>
                {tagFieldNames.map((fieldName) => (
                  <Fragment key={fieldName}>
                    {getFieldDisplayField(fieldName)}
                  </Fragment>
                ))}
              </div>
            )}

            {nonTagFieldNames.map((fieldName) => (
              <Fragment key={fieldName}>
                {getFieldDisplayField(fieldName)}
              </Fragment>
            ))}

            <ActivityToolsTable tools={activityTools} />
            <MirroredAndSharedInfoCard activity={activity} />
          </div>
        </div>
      ) : (
        <div className={classes.activityWrapper}>
          <ActivityOverviewSkeleton />
        </div>
      )}
    </div>
  );
}

export default ActivityOverviewPage;
